"use client";

import { SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type { ExploreCategory, ExploreStoryCard, FilterOption } from "../types";
import { SearchableTopicPills } from "./searchable-topic-pills";
import {
  buildTagFacets,
  buildToolFacets,
  buildTopicFacets,
  type CategoryFacet,
} from "./use-filter-facets";

// ─── Props ───────────────────────────────────────────────────

interface ExploreFilterSheetProps {
  /** All categories for this report type. */
  categories: ExploreCategory[];
  /** Currently active category (from URL segment). */
  activeCategory?: string;
  /** Currently active tag slugs (from URL query). */
  activeTags: string[];
  /** All filterable stories (already scoped to activeCategory if set). */
  filterableStories: ExploreStoryCard[];
  /** Tool/product options from filter groups. */
  toolOptions: FilterOption[];
  /** Tag display name lookup. */
  tagDisplayNames: ReadonlyMap<string, string>;

  /** Navigate to a category (URL-based). */
  onCategorySelect: (slug: string | null) => void;
  /** Toggle a tag filter. */
  onTagToggle: (slug: string) => void;
  /** Clear everything (category + tags) in a single navigation. */
  onClearAll: () => void;
}

// ─── Component ───────────────────────────────────────────────

export function ExploreFilterSheet({
  categories,
  activeCategory,
  activeTags,
  filterableStories,
  toolOptions,
  tagDisplayNames,
  onCategorySelect,
  onTagToggle,
  onClearAll,
}: ExploreFilterSheetProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeTagSet = useMemo(() => new Set(activeTags), [activeTags]);

  const activeFilterCount = (activeCategory ? 1 : 0) + activeTags.length;

  // ── Category facets ─────────────────────────────────────────
  // Always show all categories so users can switch between them.
  // On category-scoped pages filterableStories only has the active
  // category's stories, so counts for other categories would be 0
  // and misleading. We suppress counts when a category is active.
  const isCategoryScoped = activeCategory !== undefined;
  const categoryFacets = useMemo<CategoryFacet[]>(() => {
    if (isCategoryScoped) {
      // Don't compute counts — they'd be wrong for non-active categories
      return categories.map((cat) => ({
        id: cat.slug,
        label: cat.displayName,
        emoji: cat.emoji,
        count: 0,
      }));
    }
    const counts = new Map<string, number>();
    for (const story of filterableStories) {
      const slug = story.categorySlug;
      if (slug) {
        counts.set(slug, (counts.get(slug) ?? 0) + 1);
      }
    }
    return categories.map((cat) => ({
      id: cat.slug,
      label: cat.displayName,
      emoji: cat.emoji,
      count: counts.get(cat.slug) ?? 0,
    }));
  }, [categories, filterableStories, isCategoryScoped]);

  // ── Topic facets (tags + tools merged) ──────────────────────
  const tagFacets = useMemo(
    () =>
      buildTagFacets({
        storiesForTagFacets: filterableStories,
        tagDisplayNames,
      }),
    [filterableStories, tagDisplayNames]
  );

  const toolFacets = useMemo(
    () =>
      buildToolFacets({ storiesForTagFacets: filterableStories, toolOptions }),
    [filterableStories, toolOptions]
  );

  const topicFacets = useMemo(
    () => buildTopicFacets({ tagFacets, toolFacets }),
    [tagFacets, toolFacets]
  );

  const topicItems = useMemo(
    () =>
      topicFacets.map((topic) => ({
        ...topic,
        selected: activeTagSet.has(topic.id),
        onSelect: () => onTagToggle(topic.id),
      })),
    [activeTagSet, onTagToggle, topicFacets]
  );

  const hasAnyFilter = activeCategory !== undefined || activeTags.length > 0;

  return (
    <div
      className="lg:hidden"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* ── Trigger button ── */}
      <div className="mx-2 mt-3 mb-3 flex items-center gap-2">
        <button
          className="flex items-center gap-1.5 rounded-lg border-0 bg-[var(--explore-tag-bg)] px-3 py-2 font-medium text-[0.8rem] text-[var(--explore-tag-text)] transition-all active:scale-[0.97]"
          onClick={() => setIsOpen(true)}
          type="button"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[var(--explore-text-primary)] px-1 text-[0.65rem] text-[var(--explore-bg)]">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Active filter chips (quick glance + quick remove) */}
        {hasAnyFilter && (
          <div className="scrollbar-none flex items-center gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {activeCategory && (
              <button
                className="flex shrink-0 items-center gap-1 rounded-full bg-[var(--explore-chip-selected-bg)] px-2.5 py-1.5 font-medium text-[0.75rem] text-[var(--explore-chip-selected-text)] transition-all active:scale-[0.97]"
                onClick={() => onCategorySelect(null)}
                type="button"
              >
                {categories.find((c) => c.slug === activeCategory)?.emoji}{" "}
                {categories.find((c) => c.slug === activeCategory)?.displayName}
                <X className="ml-0.5 h-2.5 w-2.5 shrink-0 opacity-70" />
              </button>
            )}
            {activeTags.map((slug) => (
              <button
                className="flex shrink-0 items-center gap-1 rounded-sm bg-[var(--explore-chip-selected-bg)] px-2.5 py-1.5 font-medium text-[0.75rem] text-[var(--explore-chip-selected-text)] transition-all active:scale-[0.97]"
                key={slug}
                onClick={() => onTagToggle(slug)}
                type="button"
              >
                {tagDisplayNames.get(slug) ?? slug}
                <X className="ml-0.5 h-2.5 w-2.5 shrink-0 opacity-70" />
              </button>
            ))}
            <button
              className="shrink-0 whitespace-nowrap border-none bg-transparent p-0 font-medium text-[0.75rem] text-[var(--explore-accent-teal)]"
              onClick={onClearAll}
              type="button"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── Overlay ── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[150] bg-[var(--explore-overlay-bg)] transition-opacity"
          onClick={() => setIsOpen(false)}
          role="presentation"
        />
      )}

      {/* ── Bottom sheet ── */}
      <div
        className={cn(
          "fixed right-0 bottom-0 left-0 z-[151] flex max-h-[85vh] flex-col rounded-t-[20px] bg-[var(--explore-bg-sidebar)] transition-transform duration-400",
          isOpen ? "translate-y-0" : "translate-y-full"
        )}
        style={{
          boxShadow: "var(--explore-shadow-sheet)",
          transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        {/* Sticky header */}
        <div className="shrink-0 rounded-t-[20px] bg-[var(--explore-bg-sidebar)] px-5 pt-0 pb-4">
          {/* Drag handle */}
          <div className="mx-auto mt-2.5 mb-4 h-1 w-9 rounded-full bg-[var(--explore-border)]" />

          {/* Header row */}
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[1.1rem] text-[var(--explore-text-primary)]">
              Filters
            </h3>
            <div className="flex items-center gap-3">
              {hasAnyFilter && (
                <button
                  className="border-none bg-transparent p-0 font-medium text-[0.8rem] text-[var(--explore-accent-teal)]"
                  onClick={onClearAll}
                  type="button"
                >
                  Clear all
                </button>
              )}
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--explore-border-light)]"
                onClick={() => setIsOpen(false)}
                type="button"
              >
                <X className="h-4 w-4 text-[var(--explore-text-secondary)]" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-5 pb-8">
          {/* ── Category ── */}
          {categoryFacets.length > 0 && (
            <SheetSection title="Category">
              <div className="flex flex-wrap gap-2">
                {categoryFacets.map((cat) => (
                  <button
                    className={cn(
                      "flex items-center gap-1.5 rounded-full border-0 px-3 py-1.5 font-medium text-[0.8rem] transition-all active:scale-[0.97]",
                      activeCategory === cat.id
                        ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)]"
                        : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]"
                    )}
                    key={cat.id}
                    onClick={() => {
                      onCategorySelect(
                        activeCategory === cat.id ? null : cat.id
                      );
                    }}
                    type="button"
                  >
                    {cat.emoji && (
                      <span className="text-[0.85rem]">{cat.emoji}</span>
                    )}
                    {cat.label}
                    {cat.count > 0 && (
                      <span className="text-[0.68rem] opacity-60">
                        {cat.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </SheetSection>
          )}

          {/* ── Tags + tools ── */}
          <SheetSection title="Tags">
            <SearchableTopicPills
              emptyState="No tags or tools match the current filters."
              items={topicItems}
            />
          </SheetSection>
        </div>

        {/* Sticky footer */}
        <div className="shrink-0 border-[var(--explore-border-light)] border-t bg-[var(--explore-bg-sidebar)] px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            className="w-full rounded-xl border-none bg-[var(--explore-text-primary)] py-3.5 font-semibold text-[0.9rem] text-[var(--explore-bg)] transition-opacity active:opacity-80"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            Show results
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SheetSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <p className="mb-2.5 font-semibold text-[0.7rem] text-[var(--explore-text-muted)] uppercase tracking-[0.1em]">
        {title}
      </p>
      {children}
    </div>
  );
}
